/**
 * 지출예산편성요구 PoC (tenant·회계연도·결합코드별 localStorage)
 * - 마스터: 부서·사업·과목·재원 + 예산액(천원, 디테일 합산)
 * - 디테일: 산출레벨 1~5 · 산출내역 · 산출기초(사칙) · 산출금액(천원) · 비고
 * - 저장 키: budgetExpenseRequest|{tenant}|{fiscalYear}|{budgetKindCode}
 */

import { createFundUid } from 'budget/budgetFundStore';
import {
  STRUCTURE_BUDGET_TYPE,
  buildSubjectTree,
  getCachedSubjectsByFilter,
} from 'budget/budgetStructureStore';
import { getCachedProjects } from 'budget/budgetProjectStore';
import { rollupCalcDetails, sumTopLevelBudgetAmt } from 'utils/calcFormula';
import { syncExpenseRequestToDeptStatus } from 'budget/budgetRoundStore';
import { acctClassLabel, matchesAcctClass } from 'budget/budgetAcctStore';
import { isBdApiEnabled } from 'config/bdApiConfig';
import { commonSelect, commonSaveList, cacheGet, cacheSet } from 'services/commonQuery';

export const EXPENSE_REQ_PREFIX = 'budgetExpenseRequest';

const REQ_LINE_SAVE_QUERY = [
  'BdExpFormation.insertExpReqLine',
  'BdExpFormation.updateExpReqLine',
  'BdExpFormation.deleteExpReqLine',
];
const REQ_FUND_SAVE_QUERY = [
  'BdExpFormation.insertExpReqFund',
  'BdExpFormation.updateExpReqFund',
  'BdExpFormation.deleteExpReqFund',
];
const REQ_CALC_SAVE_QUERY = [
  'BdExpFormation.insertExpReqCalc',
  'BdExpFormation.updateExpReqCalc',
  'BdExpFormation.deleteExpReqCalc',
];

const requestCache = new Map();

export const TREE_MODE_OPTIONS = [
  { value: 'SUBJECT', label: '품목' },
  { value: 'PROJECT', label: '사업' },
];

export const CALC_LEVEL_OPTIONS = [
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' },
];

export function createRequestUid() {
  return createFundUid('ER');
}

export function createDetailId() {
  return createFundUid('ED');
}

export function expenseReqStorageKey({ tenantCode, fiscalYear, budgetKindCode }) {
  const kind = String(budgetKindCode || '').trim();
  return [EXPENSE_REQ_PREFIX, tenantCode || '', fiscalYear || '', kind].join('|');
}

export function defaultDetailRow(partial = {}) {
  const detailId = partial.detailId || createDetailId();
  return {
    detailId,
    id: detailId,
    calcLevel: 1,
    calcDesc: '',
    calcBasis: '',
    calcAmt: 0,
    remark: '',
    hasChildren: false,
    basisLocked: false,
    mod: 'A',
    ...partial,
    detailId: partial.detailId || detailId,
    id: partial.id || partial.detailId || detailId,
  };
}

export function defaultMasterRow(partial = {}) {
  const requestUid = partial.requestUid || createRequestUid();
  return {
    requestUid,
    id: requestUid,
    deptCode: '',
    deptName: '',
    projectCode: '',
    projectName: '',
    subjectUid: '',
    subjectCode: '',
    subjectDispCode: '',
    subjectName: '',
    fundUid: '',
    fundCode: '',
    fundName: '',
    fundPkgUid: '',
    fundAmounts: [],
    budgetAmt: 0,
    remark: '',
    details: [],
    mod: 'A',
    ...partial,
  };
}

function readPayload(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return Array.isArray(data?.rows) ? data.rows : null;
  } catch {
    return null;
  }
}

function writePayload(key, dims, rows) {
  localStorage.setItem(
    key,
    JSON.stringify({
      dims: {
        tenantCode: dims.tenantCode,
        fiscalYear: dims.fiscalYear,
        budgetKindCode: dims.budgetKindCode || '',
      },
      rows: rows || [],
      savedAt: new Date().toISOString(),
    })
  );
}

export function enrichMasterRow(row) {
  const requestUid = row.requestUid || createRequestUid();
  const details = rollupCalcDetails(
    (row.details || []).map((d) => {
      const detailId = d.detailId || d.id || createDetailId();
      return { ...d, detailId, id: d.id || detailId };
    }),
    { inoutDiv: '지출' }
  );
  const budgetAmt = sumTopLevelBudgetAmt(details, { inoutDiv: '지출' });
  const next = {
    ...row,
    requestUid,
    id: row.id || requestUid,
    subjectDispCode: row.subjectDispCode || row.subjectCode || '',
    fundPkgUid: row.fundPkgUid || '',
    fundAmounts: Array.isArray(row.fundAmounts) ? row.fundAmounts : [],
    details,
    budgetAmt,
  };
  // 화면 파생 최종액 유지 (저장 시 strip)
  if (next.confirmedAmt != null && next.confirmedAmt !== '') {
    next.finalAmt = (Number(next.confirmedAmt) || 0) + budgetAmt;
  }
  return next;
}

function requestCacheKey(dims) {
  return [expenseReqStorageKey(dims), String(dims?.acctClass || '').trim()].join('#');
}

/** localStorage 원본 읽기 + 회계구분 컨텍스트 필터 */
function readExpenseRequestsFromStorage(dims) {
  const key = expenseReqStorageKey(dims);
  const rows = readPayload(key);
  if (!rows) {
    writePayload(key, dims, []);
    return [];
  }
  // dims.acctClass가 있으면 회계구분 컨텍스트로 필터 (미지정 호출은 전체 — 차수 합계 등)
  const acctClass = String(dims?.acctClass || '').trim();
  const list = acctClass
    ? rows.filter((r) => matchesAcctClass(r, acctClass, dims?.tenantCode))
    : rows;
  return list.map((r) => enrichMasterRow({ ...r, mod: r.mod === 'D' ? 'D' : '' }));
}

/** 라인 + 재원구성(fund) + 산출내역(calc) 조립 — API 라인 1건 */
function attachExpReqChildren(line, funds = [], calcs = []) {
  const single = !line.fundPkgUid && funds.length === 1 ? funds[0] : null;
  return enrichMasterRow({
    ...line,
    id: line.requestUid,
    // 재원 단건은 fund 리스트 1건으로 저장 → 로드 시 마스터 재원 필드로 복원
    fundUid: single?.fundUid || '',
    fundCode: single?.fundCode || '',
    fundName: single?.fundName || '',
    fundAmounts: funds.map((f) => ({
      fundUid: f.fundUid || '',
      fundCode: f.fundCode || '',
      fundName: f.fundName || '',
      amount: Number(f.amount) || 0,
      sortOrder: Number(f.sortOrder) || 10,
    })),
    details: calcs.map((c) => ({
      detailId: c.detailId,
      id: c.detailId,
      calcLevel: Number(c.calcLevel) || 1,
      calcDesc: c.calcDesc || '',
      calcBasis: c.calcBasis || '',
      calcAmt: Number(c.calcAmt) || 0,
      remark: c.remark || '',
      // basis_locked CHAR(1) 'Y'/'N' ↔ FE boolean
      basisLocked: c.basisLocked === 'Y' || c.basisLocked === true,
      mod: '',
    })),
    mod: '',
  });
}

function groupByRequestUid(rows) {
  const map = new Map();
  (rows || []).forEach((r) => {
    const k = r?.requestUid;
    if (!k) return;
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(r);
  });
  return map;
}

/** 동기 조회용 — load 가 채운 캐시(API) 또는 localStorage(로컬) */
export function getCachedExpenseRequests(dims) {
  const kind = String(dims?.budgetKindCode || '').trim();
  if (!kind) return [];
  const key = requestCacheKey(dims);
  const cached = cacheGet(requestCache, key);
  if (cached) return cached;
  if (isBdApiEnabled()) return [];
  const rows = readExpenseRequestsFromStorage(dims);
  cacheSet(requestCache, key, rows);
  return rows;
}

export async function loadExpenseRequests(dims) {
  const kind = String(dims?.budgetKindCode || '').trim();
  if (!kind) return [];
  const key = requestCacheKey(dims);

  if (isBdApiEnabled()) {
    const lineParams = {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
      budgetKindCode: kind,
      acctClassCode: String(dims?.acctClass || '').trim() || null,
    };
    const [lines, allFunds, allCalcs] = await Promise.all([
      commonSelect('BdExpFormation.selectExpReqLineList', lineParams),
      commonSelect('BdExpFormation.selectExpReqFundListByDims', lineParams),
      commonSelect('BdExpFormation.selectExpReqCalcListByDims', lineParams),
    ]);
    const fundsBy = groupByRequestUid(allFunds);
    const calcsBy = groupByRequestUid(allCalcs);
    const rows = (lines || []).map((line) =>
      attachExpReqChildren(line, fundsBy.get(line.requestUid) || [], calcsBy.get(line.requestUid) || [])
    );
    cacheSet(requestCache, key, rows);
    return rows;
  }

  const rows = readExpenseRequestsFromStorage(dims);
  cacheSet(requestCache, key, rows);
  return rows;
}

/** 라인의 자식(재원·산출) 전량 교체 — 기존 삭제 후 재삽입 */
async function replaceExpReqChildren(row, addValue) {
  const [funds, calcs] = await Promise.all([
    commonSelect('BdExpFormation.selectExpReqFundList', { requestUid: row.requestUid }),
    commonSelect('BdExpFormation.selectExpReqCalcList', { requestUid: row.requestUid }),
  ]);

  const fundDel = funds.map((f) => ({ reqFundUid: f.reqFundUid, mod: 'D' }));
  const fundSource =
    row.fundAmounts?.length || !(row.fundUid || row.fundCode)
      ? row.fundAmounts || []
      : [{ fundUid: row.fundUid, fundCode: row.fundCode, fundName: row.fundName, amount: row.budgetAmt }];
  const fundIns = fundSource.map((f, idx) => ({
    reqFundUid: createFundUid('RF'),
    requestUid: row.requestUid,
    fundUid: f.fundUid || null,
    fundCode: f.fundCode || null,
    fundName: f.fundName || null,
    amount: Number(f.amount) || 0,
    sortOrder: (idx + 1) * 10,
    mod: 'A',
  }));
  const calcDel = calcs.map((c) => ({ detailId: c.detailId, mod: 'D' }));
  const calcIns = (row.details || []).map((d, idx) => ({
    detailId: d.detailId || createDetailId(),
    requestUid: row.requestUid,
    parentDetailId: d.parentDetailId || null,
    calcLevel: Number(d.calcLevel) || 1,
    calcDesc: d.calcDesc || null,
    calcBasis: d.calcBasis || null,
    calcAmt: Number(d.calcAmt) || 0,
    basisLocked: d.basisLocked ? 'Y' : 'N',
    sortOrder: (idx + 1) * 10,
    remark: d.remark || null,
    mod: 'A',
  }));

  await commonSaveList(REQ_FUND_SAVE_QUERY, fundDel, addValue);
  await commonSaveList(REQ_FUND_SAVE_QUERY, fundIns, addValue);
  await commonSaveList(REQ_CALC_SAVE_QUERY, calcDel, addValue);
  await commonSaveList(REQ_CALC_SAVE_QUERY, calcIns, addValue);
}

function expReqLinePersistPayload(row, dims, kind, exists) {
  return {
    requestUid: row.requestUid,
    fiscalYear: dims?.fiscalYear,
    budgetKindCode: kind,
    acctClassUid: row.acctClassUid || null,
    acctClassCode: row.acctClassCode || null,
    acctClassNm: row.acctClassNm || null,
    acctUnitUid: row.acctUnitUid || null,
    acctUnitCode: row.acctUnitCode || null,
    acctUnitNm: row.acctUnitNm || null,
    deptCode: row.deptCode || null,
    deptName: row.deptName || null,
    projectUid: row.projectUid || null,
    projectCode: row.projectCode || null,
    projectName: row.projectName || null,
    subjectUid: row.subjectUid || null,
    subjectCode: row.subjectCode || null,
    subjectDispCode: row.subjectDispCode || null,
    subjectName: row.subjectName || null,
    fundPkgUid: row.fundPkgUid || null,
    budgetAmt: Number(row.budgetAmt) || 0,
    remark: row.remark || null,
    mod: exists ? 'U' : 'A',
  };
}

export async function saveExpenseRequests(dims, rows) {
  const kind = String(dims?.budgetKindCode || '').trim();
  if (!kind) {
    throw new Error('예산차수(결합코드)가 없어 저장할 수 없습니다.');
  }
  const acctClass = String(dims?.acctClass || '').trim();
  const normalized = (rows || [])
    .filter((r) => r.mod !== 'D')
    .map((r) => {
      const enriched = enrichMasterRow(r);
      // 회계구분 스냅샷 — FormArea 컨텍스트 값을 편성 라인에 필수 기록 (BD DESIGN-006)
      const rowAcctClass = acctClass || String(enriched.acctClassCode || '').trim();
      // 전년·기정·최종 파생 필드는 저장하지 않음 (BD DESIGN-008)
      const {
        prevYearAmt: _py,
        confirmedAmt: _ca,
        finalAmt: _fa,
        ...persistable
      } = enriched;
      return {
        ...persistable,
        mod: '',
        budgetKindCode: kind,
        acctClassCode: rowAcctClass,
        acctClassNm: rowAcctClass ? acctClassLabel(rowAcctClass, dims?.tenantCode) : '',
        details: (enriched.details || [])
          .filter((d) => d.mod !== 'D')
          .map((d) => ({
            detailId: d.detailId || createDetailId(),
            id: d.detailId || d.id || createDetailId(),
            calcLevel: Math.min(5, Math.max(1, Number(d.calcLevel) || 1)),
            calcDesc: d.calcDesc || '',
            calcBasis: d.calcBasis || '',
            calcAmt: Number(d.calcAmt) || 0,
            remark: d.remark || '',
            basisLocked: !!d.basisLocked,
            mod: '',
          })),
        subjectUid: enriched.subjectUid || '',
        subjectDispCode: enriched.subjectDispCode || enriched.subjectCode || '',
        fundPkgUid: enriched.fundPkgUid || '',
        fundAmounts: Array.isArray(enriched.fundAmounts) ? enriched.fundAmounts : [],
      };
    });
  const key = expenseReqStorageKey(dims);

  if (isBdApiEnabled()) {
    const addValue = { tenantCode: dims?.tenantCode, fiscalYear: dims?.fiscalYear };
    // 라인 PK 존재 여부로 insert/update 판단 (차수 전체 기준)
    const dbLines = await commonSelect('BdExpFormation.selectExpReqLineList', {
      tenantCode: dims?.tenantCode,
      fiscalYear: dims?.fiscalYear,
      budgetKindCode: kind,
    });
    const dbUids = new Set(dbLines.map((r) => r.requestUid));

    const deleted = (rows || [])
      .filter((r) => r.mod === 'D' && r.requestUid && dbUids.has(r.requestUid))
      .map((r) => ({ requestUid: r.requestUid, mod: 'D' }));
    if (deleted.length) await commonSaveList(REQ_LINE_SAVE_QUERY, deleted, addValue);

    const linePayload = normalized.map((r) =>
      expReqLinePersistPayload(r, dims, kind, dbUids.has(r.requestUid))
    );
    if (linePayload.length) await commonSaveList(REQ_LINE_SAVE_QUERY, linePayload, addValue);
    for (const r of normalized) {
      await replaceExpReqChildren(r, addValue);
    }

    const savedApi = await loadExpenseRequests(dims);
    const allApi = acctClass
      ? await loadExpenseRequests({ ...dims, acctClass: '' })
      : savedApi;
    const deptSyncApi = await syncExpenseRequestToDeptStatus(
      dims.tenantCode,
      dims.fiscalYear,
      kind,
      allApi,
      { inoutDiv: '지출' }
    );
    return { rows: savedApi, deptSync: deptSyncApi };
  }

  // 조회 컨텍스트 밖(다른 회계구분) 라인은 그대로 보존하고 현재 컨텍스트만 교체
  const existing = readPayload(key) || [];
  const others = acctClass
    ? existing.filter((r) => !matchesAcctClass(r, acctClass, dims?.tenantCode))
    : [];
  const combined = [...others, ...normalized];
  writePayload(key, dims, combined);
  const saved = normalized.map((r) => enrichMasterRow({ ...r, mod: '' }));
  cacheSet(requestCache, requestCacheKey(dims), saved);
  // 예산차수등록 · 요구단계 부서별 조정상태에 부서 합계 반영 — 차수 전체(모든 회계구분) 기준
  const deptSync = await syncExpenseRequestToDeptStatus(
    dims.tenantCode,
    dims.fiscalYear,
    kind,
    combined.map((r) => enrichMasterRow({ ...r, mod: '' })),
    { inoutDiv: '지출' }
  );
  return { rows: saved, deptSync };
}

/** CSV·배열 → 코드 Set. 비어 있으면 null(필터 없음) */
function toFilterCodeSet(v) {
  const list = Array.isArray(v)
    ? v
    : String(v || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
  if (!list.length) return null;
  return new Set(list.map((s) => String(s).trim()).filter(Boolean));
}

export function filterExpenseRequests(rows, q = {}) {
  const deptSet = toFilterCodeSet(q.deptCode);
  const projectSet = toFilterCodeSet(q.projectCode);
  const subjectSet = toFilterCodeSet(q.subjectCode);
  const fundPkgSet = toFilterCodeSet(q.fundPkgUid);
  const fundSet = toFilterCodeSet(q.fundUid);
  const fundCodeSet = toFilterCodeSet(q.fundCode);

  return (rows || []).filter((r) => {
    if (r.mod === 'D') return false;
    if (deptSet && !deptSet.has(String(r.deptCode || '').trim())) return false;
    if (projectSet && !projectSet.has(String(r.projectCode || '').trim())) return false;
    if (subjectSet) {
      const code = String(r.subjectCode || '').trim();
      const disp = String(r.subjectDispCode || '').trim();
      if (!subjectSet.has(code) && !subjectSet.has(disp)) return false;
    }
    // 재원·재원구성: uid/코드/구성원 fundAmounts 중 하나라도 맞으면 통과 (OR)
    if (fundPkgSet || fundSet || fundCodeSet) {
      const pkg = String(r.fundPkgUid || '').trim();
      const fund = String(r.fundUid || '').trim();
      const fundCode = String(r.fundCode || '').trim();
      const pkgOk = fundPkgSet ? fundPkgSet.has(pkg) || (fundCode && fundPkgSet.has(fundCode)) : false;
      let fundOk = false;
      if (fundSet || fundCodeSet) {
        if (fundSet && fund && fundSet.has(fund)) fundOk = true;
        if (fundCodeSet && fundCode && fundCodeSet.has(fundCode)) fundOk = true;
        if (fundSet && fundCode && fundSet.has(fundCode)) fundOk = true;
        // 재원구성 스냅샷 구성원에도 검색 재원이 있으면 포함
        const amounts = Array.isArray(r.fundAmounts) ? r.fundAmounts : [];
        if (!fundOk && fundSet) {
          fundOk = amounts.some((a) => fundSet.has(String(a?.fundUid || '').trim()));
        }
      }
      if (!pkgOk && !fundOk) return false;
    }
    if (q.treeProjectCodes instanceof Set && q.treeProjectCodes.size) {
      if (!q.treeProjectCodes.has(r.projectCode)) return false;
    }
    if (q.treeSubjectKeys instanceof Set && q.treeSubjectKeys.size) {
      const k = `${r.budgetType || ''}::${r.subjectCode || ''}`;
      if (q.treeSubjectCodes instanceof Set) {
        if (!q.treeSubjectCodes.has(r.subjectCode)) return false;
      } else if (!q.treeSubjectKeys.has(k) && !q.treeSubjectKeys.has(`::${r.subjectCode}`)) {
        const hit = [...q.treeSubjectKeys].some((x) => x.endsWith(`::${r.subjectCode}`));
        if (!hit) return false;
      }
    }
    return true;
  });
}

export function validateExpenseRequests(rows = []) {
  const active = (rows || []).filter((r) => r.mod !== 'D');
  for (const r of active) {
    if (!String(r.deptCode || '').trim()) return { ok: false, message: '부서를 선택하세요.' };
    if (!String(r.projectCode || '').trim()) return { ok: false, message: '사업을 선택하세요.' };
    if (!String(r.subjectDispCode || r.subjectCode || '').trim()) {
      return { ok: false, message: '예산과목을 선택하세요.' };
    }
    if (!r.fundUid && !String(r.fundCode || '').trim() && !String(r.fundPkgUid || '').trim()) {
      return { ok: false, message: '재원 또는 재원구성을 선택하세요.' };
    }
  }
  return { ok: true };
}

export { rollupCalcDetails, sumTopLevelBudgetAmt };

function normCode(v) {
  return v == null || v === '' ? '' : String(v).trim();
}

/**
 * 마스터에 입력된 코드 + 카탈로그 상위 체인(관·항·세항… / 정책·단위·세부…)을 flat으로 수집
 */
function collectCatalogLineage(enteredList, catalog, { idKey, nameKey, budgetTypeKey = 'budgetType' }) {
  const byKey = new Map();
  for (const r of catalog || []) {
    const code = normCode(r[idKey]);
    if (!code) continue;
    const bt = normCode(r[budgetTypeKey]);
    byKey.set(`${bt}::${code}`, r);
    if (!byKey.has(`::${code}`)) byKey.set(`::${code}`, r);
  }

  const included = new Map();
  const addRow = (row) => {
    const code = normCode(row[idKey]);
    if (!code) return;
    const bt = normCode(row[budgetTypeKey]);
    const k = `${bt}::${code}`;
    if (!included.has(k)) included.set(k, { ...row, [idKey]: code, [budgetTypeKey]: bt });
  };

  for (const ent of enteredList) {
    const code = normCode(ent.code);
    if (!code) continue;
    const bt = normCode(ent.budgetType);
    let cur = byKey.get(`${bt}::${code}`) || byKey.get(`::${code}`) || null;

    if (!cur) {
      addRow({
        [idKey]: code,
        [nameKey]: ent.name || code,
        parentCode: '',
        [budgetTypeKey]: bt,
        levelName: '',
      });
      continue;
    }

    let guard = 0;
    while (cur && guard++ < 32) {
      addRow(cur);
      const parent = normCode(cur.parentCode);
      if (!parent) break;
      const rowBt = normCode(cur[budgetTypeKey] || bt);
      cur = byKey.get(`${rowBt}::${parent}`) || byKey.get(`::${parent}`);
    }
  }

  return [...included.values()].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

/**
 * 좌측 트리 — 마스터 입력 사업·과목 기준 + 확정 구조 상위 연결
 * - 품목: 입력 세목(또는 과목)에서 관·항·세항·목·세목 경로로 구성
 * - 사업: 입력 내역(또는 사업)에서 정책·단위·세부·내역 경로로 구성
 */
export function buildExpenseRequestTreeFromLines(lines = [], treeMode = 'SUBJECT', dims = {}) {
  const active = (lines || []).filter((r) => r.mod !== 'D');
  const defaultBt = normCode(dims.budgetType) || STRUCTURE_BUDGET_TYPE;

  if (treeMode === 'PROJECT') {
    const enteredMap = new Map();
    for (const r of active) {
      const code = normCode(r.projectCode);
      if (!code) continue;
      if (!enteredMap.has(code)) {
        enteredMap.set(code, {
          code,
          budgetType: normCode(r.budgetType) || defaultBt,
          name: r.projectName || code,
        });
      }
    }
    if (!enteredMap.size) return [];

    const catalog = (getCachedProjects(dims) || []).filter((row) => row.useYn !== 'N');
    const flat = collectCatalogLineage([...enteredMap.values()], catalog, {
      idKey: 'projectCode',
      nameKey: 'projectName',
    });
    const annotate = (arr) =>
      (arr || []).map((n) => ({
        ...n,
        projectCode: n.projectCode || n.subjectCode,
        projectName: n.projectName || n.subjectName,
        _treeId: `P::${n.budgetType || ''}::${n.projectCode || n.subjectCode || ''}`,
        subRows: annotate(n.subRows),
      }));
    return annotate(
      buildSubjectTree(flat, {
        idKey: 'projectCode',
        parentKey: 'parentCode',
        childrenKey: 'subRows',
      })
    );
  }

  const enteredMap = new Map();
  for (const r of active) {
    const code = normCode(r.subjectCode);
    if (!code) continue;
    const bt = normCode(r.budgetType) || defaultBt;
    const key = `${bt}::${code}`;
    if (!enteredMap.has(key)) {
      enteredMap.set(key, {
        code,
        budgetType: bt,
        name: r.subjectName || code,
      });
    }
  }
  if (!enteredMap.size) return [];

  const catalog = (getCachedSubjectsByFilter({ ...dims, inoutDiv: '지출' }) || []).filter(
    (row) => row.useYn !== 'N'
  );
  const flat = collectCatalogLineage([...enteredMap.values()], catalog, {
    idKey: 'subjectCode',
    nameKey: 'subjectName',
  });
  const annotate = (arr) =>
    (arr || []).map((n) => ({
      ...n,
      _treeId: `S::${n.budgetType || ''}::${n.subjectCode || ''}`,
      subRows: annotate(n.subRows),
    }));
  return annotate(buildSubjectTree(flat));
}
